// Self-contained Skip Intro button that monitors overview speeches

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { cn } from '@/utilities/cn';
import { useMedia, useRoomConfig, useRoomMediaElements } from '../../hooks';
import { MediaActionType } from '@airhead-games/shared/types';

export type SkipIntroButtonProps = {
  /** Classes for the animated wrapper; parent should be a column under `Poppy` (e.g. fixed top-right stack). */
  className?: string;
};

export default function SkipIntroButton({ className }: SkipIntroButtonProps) {
  const { gameId } = useRoomConfig();
  const { skipCurrentSpeech } = useMedia();
  const { getMediaElements } = useRoomMediaElements();
  const [showButton, setShowButton] = useState(false);

  // Construct overview key from gameId (e.g., "68016cb23f0914bff706b7ca.overview")
  const overviewKey = gameId ? `${gameId}.overview` : null;

  // Monitor overview speech state
  useEffect(() => {
    if (!overviewKey) return;

    const overviewSpeech = getMediaElements({
      content: overviewKey,
    })[0];

    const speechAction = overviewSpeech?.mediaAction;

    // Speech started - show button
    if (speechAction === MediaActionType.STARTED) {
      setShowButton(true);
    }

    // Speech completed - hide button
    if (speechAction === MediaActionType.COMPLETED) {
      setShowButton(false);
    }
  }, [getMediaElements, overviewKey]);

  // Handle skip button click
  const handleSkipIntro = useCallback(() => {
    skipCurrentSpeech();
  }, [skipCurrentSpeech]);

  return (
    <AnimatePresence>
      {showButton && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className={cn(
            // Flow layout below sibling Poppy; z above lectern (2001) when both exist (e.g. Ego Maniacs).
            'relative z-[2010] flex w-full shrink-0 justify-center',
            className
          )}
        >
          <Button variant="secondary" size="xl" onClick={handleSkipIntro}>
            Skip intro
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
